import { computed, Directive, input } from '@angular/core';

type ButtonSize = 'XS' | 'S' | 'M' | 'L';
type ButtonVariant = 'DEFAULT' | '270' | 'CODE' | 'WARN';

const BUTTON_CLASS_BY_SIZE: Record<ButtonSize, string[]> = {
	XS: ['px-0.5', 'py-0.5', 'rounded-0.75'],
	S: ['px-0.5', 'py-0.5', 'rounded-1'],
	M: ['px-0.75', 'py-0.75', 'rounded-1'],
	L: ['px-1', 'py-1', 'rounded-1'],
};

@Directive({
	selector: '[buttonIcon]',
	standalone: true,
	host: {
		class: 'text-m flex items-center justify-start   ',
		'[class]': '$hostClassMap()',
		'[attr.disabled]': '$disabled() ? "true" : null',
		style: '--spacing: 8px; transition: background-color 300ms, color 300ms;',
	},
})
export class ButtonIconDirective {
	public readonly $size = input<ButtonSize>('M', { alias: 'size' });
	public readonly $active = input(false, { alias: 'active' });
	public readonly $variant = input<ButtonVariant>('DEFAULT', {
		alias: 'variant',
	});

	public readonly $disabled = input<boolean, boolean | 'true' | 'false'>(false, {
		alias: 'disabled',
		transform: (value) => {
			return `${value}` === `true`;
		},
	});

	protected readonly $hostClassMap = computed(() => {
		const variant = this.$variant();
		const active = this.$active();
		const disabled = this.$disabled();
		const canHover = !disabled && !active;

		// Size classes
		const classMap: Record<string, boolean> = {};
		for (const cls of BUTTON_CLASS_BY_SIZE[this.$size()] ?? []) {
			classMap[cls] = true;
		}

		// Text color (default state)
		classMap['fc-400'] = !active && variant === 'DEFAULT';
		classMap['fc-500'] = !active && variant === '270';
		classMap['text-primary'] = active;
		classMap['fc-100'] = variant === 'CODE';
		classMap['text-warn'] = variant === 'WARN';

		// Text color (hover)
		classMap['hover:text-primary-500'] = canHover && variant === 'CODE';
		classMap['hover:fc-800'] = canHover && variant !== 'CODE';

		// Background (default)
		classMap['hover:bg-standard-hover'] = canHover && variant === 'DEFAULT';
		classMap['bg-270'] = variant === '270';
		classMap['hover-always:overlay-standard-hover-270'] = canHover && variant === '270';
		classMap['bg-code'] = variant === 'CODE';
		classMap['hover:bg-primary/20'] = canHover && variant === 'CODE';
		classMap['hover:bg-warn/20'] = canHover && variant === 'WARN';
		classMap['bg-primary/20'] = active;

		return classMap;
	});
}
